import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SwotifyLoader, { LOADER_SHOW_DELAY_MS } from '../ui/SwotifyLoader';

/**
 * Full-app loader while a client navigation is still outstanding.
 * Arms on in-app link clicks; only becomes visible if the destination
 * hasn’t finished committing before LOADER_SHOW_DELAY_MS.
 */
export default function AppNavigationLoader() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const showTimerRef = useRef(undefined);
  const hideTimerRef = useRef(undefined);
  const armedAtRef = useRef(0);
  const locRef = useRef(`${location.pathname}${location.search}${location.hash}`);

  const clearShow = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = undefined;
    }
  };

  const clearHide = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = undefined;
    }
  };

  useEffect(() => {
    const key = `${location.pathname}${location.search}${location.hash}`;
    const prev = locRef.current;
    locRef.current = key;
    if (key === prev) {
      // Same-URL commit — e.g. React Router re-navigating to the current route
      // when the nav link for the page you're already on is clicked. There is
      // no view change to wait for, so cancel any armed/visible loader instead
      // of leaving it to spin forever.
      clearShow();
      clearHide();
      queueMicrotask(() => setVisible(false));
      armedAtRef.current = 0;
      return undefined;
    }

    // Destination committed. If we never reached the delay, cancel — fast nav.
    // If the loader is already up (or about to be), tuck it away shortly after.
    const armedAgo = Date.now() - armedAtRef.current;
    const wasArmed = armedAtRef.current > 0 && armedAgo < 15000;
    armedAtRef.current = 0;

    if (!wasArmed) {
      clearShow();
      queueMicrotask(() => setVisible(false));
      return undefined;
    }

    if (armedAgo < LOADER_SHOW_DELAY_MS) {
      // Fast: URL/view settled before the delay — never show.
      clearShow();
      queueMicrotask(() => setVisible(false));
      return undefined;
    }

    // Slow: loader should be visible (or showing now). Keep briefly, then hide.
    clearShow();
    queueMicrotask(() => setVisible(true));
    clearHide();
    hideTimerRef.current = setTimeout(() => setVisible(false), 350);
    return () => clearHide();
  }, [location.pathname, location.search, location.hash, location.key]);

  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;

      const href = a.getAttribute('href');
      if (!href || href === '#' || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (/^https?:\/\//i.test(href) && !href.includes(window.location.host)) return;

      // Ignore pure hash jumps on the same route
      if (href.startsWith('#') && !href.startsWith('#/')) return;

      // Ignore clicks on links pointing to current path & search
      try {
        const linkUrl = new URL(a.href, window.location.href);
        if (linkUrl.pathname === window.location.pathname && linkUrl.search === window.location.search) {
          return;
        }
      } catch {
        /* ignore invalid URL parse */
      }

      clearHide();
      clearShow();
      armedAtRef.current = Date.now();
      showTimerRef.current = setTimeout(() => {
        setVisible(true);
      }, LOADER_SHOW_DELAY_MS);
    };

    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      clearShow();
      clearHide();
    };
  }, []);

  if (!visible) return null;

  return (
    <SwotifyLoader
      variant="fullscreen"
      label="Loading page…"
      showBrand
    />
  );
}
